// add boilder plate for me

import React, { useState, useEffect } from 'react';
import { addDoc, collection, query, where, getDocs, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../userContext';
import { Link, useNavigate } from 'react-router-dom';
import { UserCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useClient } from '../clientContext';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ClientHome = () => {
  const [companyName, setCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { clients, refreshClients } = useClient();
  const { user, businessProfile, userProfile } = useUser();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isImpersonating = businessProfile?.id !== userProfile?.companyId;
  
  if (isImpersonating) {
    return (
      <div className="p-6 space-y-6 w-full" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        <div className="w-full border-l-4 p-4 rounded-lg" style={{
          background: 'rgba(241, 196, 15, 0.2)',
          borderColor: '#F1C40F',
          color: '#F39C12'
        }} role="alert">
          <p className="font-bold">NOT APPLICABLE</p>
          <p>Client management is not available while impersonating a client.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const newCompany = {
        name: companyName,
        parentCompany: businessProfile.id,
        type: 'client',
        createdAt: new Date(),
      };

      await addDoc(collection(db, 'company'), newCompany);
      setCompanyName('');
      setSuccess(true);
      await refreshClients();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to create company. Please try again.');
      console.error('Error creating company:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return 'N/A';
  };

  const handleDeleteClient = async (client) => {
    setClientToDelete(client);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;
    
    setIsDeleting(true);
    try {
      const batch = writeBatch(db);
      
      // Delete all campaigns associated with this client
      const campaignsQuery = query(
        collection(db, 'campaigns'),
        where('companyId', '==', clientToDelete.id)
      );
      const campaignsSnapshot = await getDocs(campaignsQuery);
      campaignsSnapshot.docs.forEach((campaignDoc) => {
        batch.delete(doc(db, 'campaigns', campaignDoc.id));
      });
      
      // Delete the client
      batch.delete(doc(db, 'company', clientToDelete.id));
      
      // Commit the batch
      await batch.commit();
      
      // Refresh the clients list
      await refreshClients();
      setDeleteModalOpen(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client. Please try again.');
    } finally {
      setIsDeleting(false);
      setClientToDelete(null);
    }
  };

  const renderClientListItem = (client) => (
    <li key={client.id}>
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center min-w-0 flex-1">
          <div className="flex-shrink-0">
            {client.headlinePhoto ? (
              <img
                src={client.headlinePhoto}
                alt={`${client.name} logo`}
                className="h-8 w-8 rounded-lg object-contain"
                style={{background: 'rgba(52, 73, 94, 0.8)'}}
              />
            ) : (
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{
                background: 'rgba(155, 89, 182, 0.2)',
                border: '1px solid rgba(155, 89, 182, 0.4)'
              }}>
                <UserCircleIcon className="h-5 w-5" style={{color: '#9B59B6'}} />
              </div>
            )}
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <h2 className="text-sm font-bold truncate" style={{color: '#ECF0F1'}}>
              {client.name}
            </h2>
            <div className="flex items-center text-xs font-bold" style={{color: '#BDC3C7'}}>
              <span>CREATED {formatDate(client.createdAt).toUpperCase()}</span>
            </div>
          </div>
        </div>
        <div className="ml-4 flex items-center space-x-2">
          <button
            onClick={() => navigate(`/studio/edit-client/${client.id}`)}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-100"
            style={{
              background: 'linear-gradient(135deg, #2ECC71, #27AE60)',
              border: '2px solid rgba(241, 196, 15, 0.4)',
              color: '#FFFFFF',
              textShadow: '1px 1px 0px #1E8449'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #27AE60, #1E8449)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #2ECC71, #27AE60)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            MANAGE
          </button>
          <button
            onClick={() => handleDeleteClient(client)}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-100"
            style={{
              background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
              border: '2px solid rgba(231, 76, 60, 0.4)',
              color: '#FFFFFF',
              textShadow: '1px 1px 0px #A93226'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            DELETE
          </button>
        </div>
      </div>
    </li>
  );

  return (
    <>
      <div className="sm:px-4 pb-5 max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        {/* Header */}
        <div className="mb-5 pb-3 flex justify-between items-center">
          <h1 className="text-2xl md:text-3xl font-black tracking-wide" style={{
            color: '#F1C40F',
            textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
          }}>CLIENTS</h1>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* New Client Form - Now in a side panel */}
          <div className="md:col-span-1">
            <div className="rounded-lg p-6 sticky top-6" style={{
              background: 'linear-gradient(135deg, #2C3E50, #34495E)',
              border: '2px solid rgba(241, 196, 15, 0.4)'
            }}>
              <h2 className="text-lg font-black tracking-wide mb-4" style={{
                color: '#F1C40F',
                textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
              }}>CREATE NEW CLIENT</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-bold mb-2" style={{color: '#BDC3C7'}}>
                      COMPANY NAME
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="block w-full rounded-lg py-2.5 px-3 text-sm font-bold transition-all duration-200"
                      style={{
                        background: 'rgba(52, 73, 94, 0.8)',
                        border: '2px solid rgba(241, 196, 15, 0.3)',
                        color: '#ECF0F1'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#F1C40F';
                        e.target.style.boxShadow = '0 0 8px rgba(241, 196, 15, 0.3)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(241, 196, 15, 0.3)';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="Enter company name"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2.5 rounded-lg text-sm font-bold transition-all duration-100"
                    style={!isLoading ? {
                      background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                      border: '2px solid rgba(241, 196, 15, 0.6)',
                      color: '#FFFFFF',
                      textShadow: '1px 1px 0px #A93226'
                    } : {
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(149, 165, 166, 0.3)',
                      color: '#95A5A6',
                      cursor: 'not-allowed'
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                        e.target.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading) {
                        e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        CREATING...
                      </>
                    ) : (
                      'CREATE CLIENT'
                    )}
                  </button>
                </div>
              </form>
              {error && (
                <div className="mt-4 p-3 rounded-lg" style={{
                  background: 'rgba(231, 76, 60, 0.2)',
                  border: '1px solid rgba(231, 76, 60, 0.4)'
                }}>
                  <p className="text-sm font-bold" style={{color: '#E74C3C'}}>{error}</p>
                </div>
              )}
              {success && (
                <div className="mt-4 p-3 rounded-lg" style={{
                  background: 'rgba(46, 204, 113, 0.2)',
                  border: '1px solid rgba(46, 204, 113, 0.4)'
                }}>
                  <p className="text-sm font-bold" style={{color: '#2ECC71'}}>CLIENT CREATED SUCCESSFULLY!</p>
                </div>
              )}
            </div>
          </div>

          {/* Clients List - Now in main content area */}
          <div className="md:col-span-2">
            <div className="rounded-lg" style={{
              background: 'linear-gradient(135deg, #2C3E50, #34495E)',
              border: '2px solid rgba(241, 196, 15, 0.4)'
            }}>
              {clients.length > 0 ? (
                <ul className="divide-y" style={{borderColor: 'rgba(241, 196, 15, 0.2)'}}>
                  {clients.map(renderClientListItem)}
                </ul>
              ) : (
                <div className="text-center py-12">
                  <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto" style={{
                    background: 'rgba(155, 89, 182, 0.2)',
                    border: '2px solid rgba(155, 89, 182, 0.4)'
                  }}>
                    <UserCircleIcon className="h-10 w-10" style={{color: '#9B59B6'}} />
                  </div>
                  <h3 className="mt-4 text-base font-black tracking-wide" style={{color: '#ECF0F1'}}>NO CLIENTS YET</h3>
                  <p className="mt-1 text-sm font-bold" style={{color: '#BDC3C7'}}>
                    Create your first client using the form
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Transition.Root show={deleteModalOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setDeleteModalOpen}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Delete Client
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Are you sure you want to delete {clientToDelete?.name}? This action cannot be undone and will delete all associated campaigns.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={confirmDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ClientHome;
